<script setup lang="ts">
import type { ArticleGuideNavItem } from '~/components/Article/ArticleInThisGuide.vue';
import type { ArticleTemplateKeepReading } from '~/components/Article/articleTemplates';
import type { ArticleData } from '~/graphql/generated/graphql';
import type { Article } from '~/types/Learn/Article';

import ArticleHeader from '~/components/Article/ArticleHeader.vue';
import ArticleInThisGuide from '~/components/Article/ArticleInThisGuide.vue';
import ArticleKeepReading from '~/components/Article/ArticleKeepReading.vue';
import { ARTICLE_TEMPLATES } from '~/components/Article/articleTemplates';
import { useIaLayout } from '~/composables/unified/useIaBackFrame';
import { UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';

/**
 * The page furniture every article template shares — grid, header, sidebar TOC,
 * cover image, Keep Reading and the sign-up CTA. Templates supply only their body
 * via the default slot, so a change to the shared skeleton lands once, not once
 * per template. The header badge comes from the template registry, so templates
 * never pass their own naming.
 */
interface Props {
  article: Article;
  data: ArticleData | null;
  guideItems: ArticleGuideNavItem[];
  /** Heading for the sidebar TOC; templates that relabel the pill bar pass the same label here. */
  guideLabel?: string;
  preview?: boolean;
  keepReading?: ArticleTemplateKeepReading[];
  signUpLink?: string;
  showSignUpCta?: boolean;
  /** The quiz landing has no page hero — its quiz card carries the visual instead. */
  showCoverImage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  guideLabel: undefined,
  preview: false,
  keepReading: () => [],
  signUpLink: undefined,
  showSignUpCta: true,
  showCoverImage: true,
});

const emit = defineEmits<{
  signUpClick: [];
}>();

defineSlots<{
  /** Rendered only when data is non-null (the root v-if), so the slot narrows it. */
  default: (props: { data: ArticleData }) => unknown;
}>();

const { t } = useI18n();

const badge = computed(() => {
  const badgeKey = props.data ? ARTICLE_TEMPLATES[props.data.template]?.badgeKey : null;
  return badgeKey ? t(badgeKey) : undefined;
});

const iaLayout = useIaLayout();
</script>

<template>
  <div
    v-if="data"
    class="mx-auto grid w-full max-w-7xl grid-cols-1 gap-x-10 px-4 pb-10 lg:grid-cols-4"
    :class="{ 'pt-10 lg:px-8': !iaLayout }"
  >
    <ArticleHeader
      :article="article"
      :data="data"
      :badge="badge"
    />

    <aside
      v-if="guideItems.length > 0"
      class="my-6 lg:col-start-4 lg:row-start-1 lg:row-span-2 lg:my-0"
    >
      <ArticleInThisGuide
        :items="guideItems"
        :label="guideLabel"
        class="lg:sticky lg:top-8"
      />
    </aside>

    <div class="min-w-0 max-w-3xl lg:col-span-3 lg:col-start-1">
      <NuxtImg
        v-if="showCoverImage && article.coverImage?.url"
        :src="article.coverImage.url"
        :alt="article.name"
        width="1280"
        height="720"
        fetchpriority="high"
        class="my-8 aspect-video w-full rounded-2xl object-cover"
      />

      <slot :data="data" />

      <ArticleKeepReading :articles="keepReading" class="mt-12" />
      <KUIUnifiedSignUpCtaCard
        v-if="showSignUpCta"
        :to="signUpLink ?? UNIFIED_ROUTE_LOGIN"
        :title="$t('unified.signUpCta.article.title')"
        :button-label="$t('unified.signUpCta.article.button')"
        is-blinky
        :disabled="preview"
        class="mt-8"
        @click="emit('signUpClick')"
      />
    </div>
  </div>
</template>
